import React, { useEffect, useState, useCallback } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import { useAdminAuth } from '../../context/AdminAuthContext';
import {
  getAdmins, createAdmin, updateAdmin,
  updateAdminPermissions, toggleAdminActive,
  resetAdminPassword, deleteAdmin
} from '../../services/adminApi';
import { toast } from 'react-toastify';

// ── Constants ─────────────────────────────────────────────────
const ROLES = [
  { value: 'super_admin', label: 'Super Admin',
    desc: 'Full system access. Can manage other admins.' },
  { value: 'admin',       label: 'Admin',
    desc: 'Standard access. Cannot manage admins.' },
  { value: 'support',     label: 'Support',
    desc: 'Read-only access for customer support.' },
];

const ROLE_COLORS = {
  super_admin: 'badge-purple',
  admin:       'badge-blue',
  support:     'badge-grey',
};

// Default permissions template for each role
const DEFAULT_PERMISSIONS = {
  super_admin: {
    users:     { view: true,  edit: true },
    providers: { view: true,  edit: true,  delete: true },
    assets:    { view: true,  create: true, invalidate: true, manage_inventory: true }
  },
  admin: {
    users:     { view: true,  edit: true },
    providers: { view: true,  edit: true,  delete: false },
    assets:    { view: true,  create: true, invalidate: true, manage_inventory: true }
  },
  support: {
    users:     { view: true,  edit: false },
    providers: { view: true,  edit: false, delete: false },
    assets:    { view: true,  create: false, invalidate: false, manage_inventory: false }
  }
};

// Permission labels for the UI
const PERMISSION_LABELS = {
  users:     { view: 'View users',     edit: 'Edit user profiles' },
  providers: { view: 'View providers', edit: 'Edit providers',    delete: 'Delete providers' },
  assets:    {
    view:             'View QR codes',
    create:           'Generate QR batches',
    invalidate:       'Invalidate QR codes',
    manage_inventory: 'Manage inventory'
  }
};

// ── Permission toggle grid ────────────────────────────────────
function PermissionsEditor({ permissions, onChange, readOnly }) {
  const toggle = (resource, action) => {
    if (readOnly) return;
    onChange({
      ...permissions,
      [resource]: {
        ...permissions[resource],
        [action]: !permissions[resource]?.[action]
      }
    });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {Object.entries(PERMISSION_LABELS).map(([resource, actions]) => (
        <div key={resource}>
          <div style={{
            fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
            letterSpacing: '.8px', color: 'var(--text3)', marginBottom: 8
          }}>
            {resource}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {Object.entries(actions).map(([action, label]) => {
              const on = permissions?.[resource]?.[action] ?? false;
              return (
                <div key={action} style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '8px 12px',
                  background: 'var(--surface2)',
                  borderRadius: 'var(--radius)',
                  border: `1px solid ${on ? 'var(--accent-dim)' : 'var(--border)'}`,
                  opacity: readOnly ? .6 : 1
                }}>
                  <span style={{ fontSize: 13, color: 'var(--text)' }}>{label}</span>
                  <button
                    type="button"
                    onClick={() => toggle(resource, action)}
                    disabled={readOnly}
                    style={{
                      width: 38, height: 20,
                      background: on ? 'var(--accent)' : 'var(--surface3)',
                      border: 'none', borderRadius: 10,
                      cursor: readOnly ? 'default' : 'pointer',
                      position: 'relative', transition: 'background .2s', padding: 0
                    }}
                  >
                    <span style={{
                      position: 'absolute', top: 2,
                      left: on ? 20 : 2,
                      width: 16, height: 16,
                      background: '#fff', borderRadius: '50%',
                      transition: 'left .2s',
                      boxShadow: '0 1px 3px rgba(0,0,0,.3)'
                    }} />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      ))}
      {readOnly && (
        <p style={{ fontSize: 12, color: 'var(--text3)', margin: 0 }}>
          Super admin permissions cannot be edited.
        </p>
      )}
    </div>
  );
}

// ── Invite / create modal ─────────────────────────────────────
function InviteModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    firstName: '', lastName: '', email: '',
    password: '', confirmPassword: '',
    role: 'admin'
  });
  const [permissions, setPermissions] = useState(DEFAULT_PERMISSIONS.admin);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  // Update permission defaults when role changes
  const handleRoleChange = role => {
    setForm(f => ({ ...f, role }));
    setPermissions(DEFAULT_PERMISSIONS[role] || DEFAULT_PERMISSIONS.admin);
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setError('');
    if (form.password !== form.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (form.password.length < 12) {
      setError('Admin passwords must be at least 12 characters');
      return;
    }
    setLoading(true);
    try {
      const res = await createAdmin({
        firstName:   form.firstName,
        lastName:    form.lastName,
        email:       form.email,
        password:    form.password,
        role:        form.role,
        permissions: form.role === 'super_admin' ? undefined : permissions
      });
      toast.success(`${res.data.admin.firstName} ${res.data.admin.lastName} invited`);
      onCreated(res.data.admin);
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create admin');
    } finally { setLoading(false); }
  };

  const field = (label, key, type = 'text', required = true) => (
    <div>
      <label style={{ display: 'block', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.6px', color: 'var(--text3)', marginBottom: 5 }}>
        {label}
      </label>
      <input
        className="admin-input"
        type={type}
        value={form[key]}
        onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
        required={required}
        autoComplete={type === 'password' ? 'new-password' : undefined}
      />
    </div>
  );

  return (
    <div className="modal-backdrop" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 560 }}>
        <div className="modal-header">
          <h3>Invite Administrator</h3>
          <button className="btn-icon" onClick={onClose}>✕</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {error && (
              <div style={{ background: 'var(--red-dim)', border: '1px solid #7f1d1d', color: 'var(--red)', borderRadius: 'var(--radius)', padding: '10px 14px', fontSize: 13 }}>
                ⚠ {error}
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              {field('First Name', 'firstName')}
              {field('Last Name', 'lastName')}
            </div>
            {field('Email', 'email', 'email')}

            <div>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.6px', color: 'var(--text3)', marginBottom: 8 }}>
                Role
              </label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {ROLES.map(r => (
                  <label key={r.value} style={{
                    display: 'flex', alignItems: 'flex-start', gap: 10,
                    padding: '10px 12px',
                    background: form.role === r.value ? 'var(--accent-glow)' : 'var(--surface2)',
                    border: `1px solid ${form.role === r.value ? 'var(--accent-dim)' : 'var(--border)'}`,
                    borderRadius: 'var(--radius)', cursor: 'pointer'
                  }}>
                    <input
                      type="radio"
                      name="role"
                      value={r.value}
                      checked={form.role === r.value}
                      onChange={() => handleRoleChange(r.value)}
                      style={{ marginTop: 3 }}
                    />
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text)' }}>{r.label}</div>
                      <div style={{ fontSize: 12, color: 'var(--text3)' }}>{r.desc}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {form.role !== 'super_admin' && (
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.8px', color: 'var(--text3)', marginBottom: 10 }}>
                  Permissions
                </div>
                <PermissionsEditor
                  permissions={permissions}
                  onChange={setPermissions}
                  readOnly={false}
                />
              </div>
            )}

            {field('Password (min 12 chars)', 'password', 'password')}
            {field('Confirm Password', 'confirmPassword', 'password')}
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? <span className="spinner" /> : 'Send Invite'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Edit admin modal ──────────────────────────────────────────
function EditModal({ adminId, currentAdminId, onClose, onUpdated }) {
  const [data, setData]         = useState(null);
  const [tab, setTab]           = useState('details');
  const [form, setForm]         = useState({ firstName: '', lastName: '', role: 'admin' });
  const [permissions, setPerms] = useState({});
  const [newPassword, setNewPassword] = useState('');
  const [confirmPw, setConfirmPw]     = useState('');
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');

  useEffect(() => {
    if (!adminId) return;
    getAdmins().then(res => {
      const a = res.data.admins.find(x => x._id === adminId);
      if (a) {
        setData(a);
        setForm({ firstName: a.firstName, lastName: a.lastName, role: a.role });
        setPerms(a.permissions || {});
      }
    }).catch(err => {
      setError(err.response?.data?.message || 'Failed to load admin');
    });
  }, [adminId]);

  const handleSaveDetails = async () => {
    setLoading(true); setError('');
    try {
      await updateAdmin(adminId, { firstName: form.firstName, lastName: form.lastName, role: form.role });
      toast.success('Details updated');
      onUpdated();
    } catch (err) { setError(err.response?.data?.message || 'Failed to update'); }
    finally { setLoading(false); }
  };

  const handleSavePermissions = async () => {
    setLoading(true); setError('');
    try {
      await updateAdminPermissions(adminId, permissions);
      toast.success('Permissions updated');
      onUpdated();
    } catch (err) { setError(err.response?.data?.message || 'Failed to update permissions'); }
    finally { setLoading(false); }
  };

  const handleResetPassword = async () => {
    setError('');
    if (newPassword !== confirmPw) { setError('Passwords do not match'); return; }
    if (newPassword.length < 12) { setError('Admin passwords must be at least 12 characters'); return; }
    setLoading(true);
    try {
      await resetAdminPassword(adminId, newPassword);
      toast.success('Password updated');
      setNewPassword(''); setConfirmPw('');
    } catch (err) { setError(err.response?.data?.message || 'Failed to reset password'); }
    finally { setLoading(false); }
  };

  const handleToggleActive = async () => {
    const action = data?.isActive ? 'disable' : 'enable';
    if (!window.confirm(`Are you sure you want to ${action} this admin?`)) return;
    setLoading(true);
    try {
      await toggleAdminActive(adminId);
      toast.success(`Admin ${action}d`);
      onUpdated();
      onClose();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setLoading(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm('Permanently delete this admin account? This cannot be undone.')) return;
    setLoading(true);
    try {
      await deleteAdmin(adminId);
      toast.success('Admin deleted');
      onUpdated();
      onClose();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setLoading(false); }
  };

  const isSelf       = adminId === currentAdminId;
  const isSuperAdmin = data?.role === 'super_admin';

  const TABS = ['details', 'permissions', 'password', 'danger'];

  return (
    <div className="modal-backdrop" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 540 }}>
        <div className="modal-header">
          <h3>Edit Administrator</h3>
          {data && <span className={`badge ${ROLE_COLORS[data.role] || 'badge-grey'}`}>{data.role.replace('_', ' ')}</span>}
          <button className="btn-icon" style={{ marginLeft: 'auto' }} onClick={onClose}>✕</button>
        </div>

        {!data ? (
          <div className="modal-body"><div className="empty-state"><div className="spinner" /></div></div>
        ) : (
          <>
            <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid var(--border)', padding: '0 20px' }}>
              {TABS.map(t => (
                <button key={t} onClick={() => setTab(t)} style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  padding: '10px 14px', fontSize: 12, fontWeight: 600, textTransform: 'capitalize',
                  color: tab === t ? 'var(--accent)' : 'var(--text3)',
                  borderBottom: tab === t ? '2px solid var(--accent)' : '2px solid transparent',
                  marginBottom: -1, fontFamily: 'inherit'
                }}>
                  {t === 'danger' ? '⚠ Danger' : t}
                </button>
              ))}
            </div>

            <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {error && (
                <div style={{ background: 'var(--red-dim)', border: '1px solid #7f1d1d', color: 'var(--red)', borderRadius: 'var(--radius)', padding: '10px 14px', fontSize: 13 }}>
                  ⚠ {error}
                </div>
              )}

              {/* Details tab */}
              {tab === 'details' && (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    {[['First Name', 'firstName'], ['Last Name', 'lastName']].map(([lbl, key]) => (
                      <div key={key}>
                        <label style={{ display: 'block', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.6px', color: 'var(--text3)', marginBottom: 5 }}>{lbl}</label>
                        <input className="admin-input" value={form[key]} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))} />
                      </div>
                    ))}
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.6px', color: 'var(--text3)', marginBottom: 5 }}>Email</label>
                    <input className="admin-input" value={data.email} disabled />
                  </div>

                  {!isSelf && (
                    <div>
                      <label style={{ display: 'block', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.6px', color: 'var(--text3)', marginBottom: 8 }}>Role</label>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {ROLES.map(r => (
                          <label key={r.value} style={{
                            display: 'flex', alignItems: 'flex-start', gap: 10,
                            padding: '8px 12px',
                            background: form.role === r.value ? 'var(--accent-glow)' : 'var(--surface2)',
                            border: `1px solid ${form.role === r.value ? 'var(--accent-dim)' : 'var(--border)'}`,
                            borderRadius: 'var(--radius)', cursor: 'pointer'
                          }}>
                            <input type="radio" name="editRole" value={r.value} checked={form.role === r.value} onChange={() => setForm(f => ({ ...f, role: r.value }))} style={{ marginTop: 3 }} />
                            <div>
                              <div style={{ fontWeight: 600, fontSize: 13 }}>{r.label}</div>
                              <div style={{ fontSize: 12, color: 'var(--text3)' }}>{r.desc}</div>
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}

                  <div style={{ display: 'flex', gap: 8, paddingTop: 4 }}>
                    <div style={{ fontSize: 12, color: 'var(--text3)', flex: 1 }}>
                      Last login: {data.lastLogin ? new Date(data.lastLogin).toLocaleString() : 'Never'}<br />
                      Created: {new Date(data.createdAt).toLocaleDateString()}
                    </div>
                    <button className="btn btn-primary" onClick={handleSaveDetails} disabled={loading}>
                      {loading ? <span className="spinner" /> : 'Save'}
                    </button>
                  </div>
                </>
              )}

              {/* Permissions tab */}
              {tab === 'permissions' && (
                <>
                  <PermissionsEditor
                    permissions={permissions}
                    onChange={setPerms}
                    readOnly={isSuperAdmin}
                  />
                  {!isSuperAdmin && (
                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <button className="btn btn-primary" onClick={handleSavePermissions} disabled={loading}>
                        {loading ? <span className="spinner" /> : 'Save Permissions'}
                      </button>
                    </div>
                  )}
                </>
              )}

              {/* Password tab */}
              {tab === 'password' && (
                <>
                  <p style={{ fontSize: 13, color: 'var(--text3)', margin: 0 }}>
                    Set a new password for this admin account. Minimum 12 characters.
                  </p>
                  {[['New Password', newPassword, setNewPassword], ['Confirm Password', confirmPw, setConfirmPw]].map(([lbl, val, set]) => (
                    <div key={lbl}>
                      <label style={{ display: 'block', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.6px', color: 'var(--text3)', marginBottom: 5 }}>{lbl}</label>
                      <input className="admin-input" type="password" value={val} onChange={e => set(e.target.value)} autoComplete="new-password" />
                    </div>
                  ))}
                  <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <button className="btn btn-primary" onClick={handleResetPassword} disabled={loading || !newPassword}>
                      {loading ? <span className="spinner" /> : 'Update Password'}
                    </button>
                  </div>
                </>
              )}

              {/* Danger tab */}
              {tab === 'danger' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {/* Enable / Disable */}
                  {!isSelf && (
                    <div style={{ background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 14 }}>
                      <div style={{ fontWeight: 600, marginBottom: 6, fontSize: 13 }}>
                        Account Access
                      </div>
                      <p style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 10 }}>
                        {data.isActive
                          ? 'Disabling prevents this admin from logging in. They can be re-enabled at any time.'
                          : 'This account is currently disabled. Re-enable to allow login.'}
                      </p>
                      <button
                        className={`btn btn-sm ${data.isActive ? 'btn-danger' : 'btn-success'}`}
                        onClick={handleToggleActive}
                        disabled={loading}
                      >
                        {data.isActive ? '⊘ Disable Account' : '↺ Enable Account'}
                      </button>
                    </div>
                  )}

                  {/* Delete */}
                  {!isSelf && (
                    <div style={{ background: 'var(--red-dim)', border: '1px solid #7f1d1d', borderRadius: 'var(--radius)', padding: 14 }}>
                      <div style={{ fontWeight: 600, color: 'var(--red)', marginBottom: 6, fontSize: 13 }}>Delete Account</div>
                      <p style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 10 }}>
                        Permanently removes this admin account. This cannot be undone.
                      </p>
                      <button className="btn btn-sm btn-danger" onClick={handleDelete} disabled={loading}>
                        🗑 Delete Permanently
                      </button>
                    </div>
                  )}

                  {isSelf && (
                    <p style={{ fontSize: 13, color: 'var(--text3)', textAlign: 'center', padding: '20px 0' }}>
                      You cannot disable or delete your own account.
                    </p>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────
export default function AdminManagement() {
  const { admin: currentAdmin } = useAdminAuth();
  const [admins, setAdmins]     = useState([]);
  const [loading, setLoading]   = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [editId, setEditId]     = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    getAdmins()
      .then(r => setAdmins(r.data.admins))
      .catch(() => toast.error('Failed to load admins'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const isSuperAdmin = currentAdmin?.role === 'super_admin';

  return (
    <AdminLayout title="Administrators">

      <div className="toolbar" style={{ marginBottom: 16 }}>
        <div style={{ flex: 1 }}>
          <span style={{ fontSize: 12, color: 'var(--text3)' }}>
            {admins.length} admin account{admins.length !== 1 ? 's' : ''}
          </span>
        </div>
        {isSuperAdmin && (
          <button className="btn btn-primary" onClick={() => setShowInvite(true)}>
            + Invite Admin
          </button>
        )}
      </div>

      {!isSuperAdmin && (
        <div style={{ background: 'var(--yellow-dim)', border: '1px solid #78350f', color: 'var(--yellow)', borderRadius: 'var(--radius)', padding: '10px 16px', fontSize: 13, marginBottom: 16 }}>
          ⚠ Only super administrators can manage other admins. You can view the list below.
        </div>
      )}

      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Status</th>
              <th>Last Login</th>
              <th>Created By</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              Array.from({ length: 4 }).map((_, i) => (
                <tr key={i}>
                  {Array.from({ length: 7 }).map((_, j) => (
                    <td key={j}><div className="skeleton" style={{ height: 14, width: '80%' }} /></td>
                  ))}
                </tr>
              ))
            ) : admins.length === 0 ? (
              <tr>
                <td colSpan={7}>
                  <div className="empty-state">
                    <div className="empty-icon">👥</div>
                    <p>No admins found</p>
                  </div>
                </td>
              </tr>
            ) : admins.map(a => {
              const isSelf = a._id === currentAdmin?._id;
              return (
                <tr key={a._id} style={{ opacity: a.isActive ? 1 : .5 }}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{
                        width: 28, height: 28, borderRadius: '50%',
                        background: 'var(--accent-dim)', color: 'var(--accent)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 11, fontWeight: 700, flexShrink: 0
                      }}>
                        {a.firstName?.[0]}{a.lastName?.[0]}
                      </div>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 13 }}>
                          {a.firstName} {a.lastName}
                          {isSelf && <span style={{ fontSize: 10, color: 'var(--text3)', marginLeft: 6 }}>(you)</span>}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td style={{ fontSize: 12, color: 'var(--text2)' }}>{a.email}</td>
                  <td>
                    <span className={`badge ${ROLE_COLORS[a.role] || 'badge-grey'}`}>
                      {a.role.replace('_', ' ')}
                    </span>
                  </td>
                  <td>
                    <span className={`badge ${a.isActive ? 'badge-green' : 'badge-red'}`}>
                      {a.isActive ? 'Active' : 'Disabled'}
                    </span>
                  </td>
                  <td style={{ fontSize: 12, color: 'var(--text3)' }}>
                    {a.lastLogin ? new Date(a.lastLogin).toLocaleDateString() : '—'}
                  </td>
                  <td style={{ fontSize: 12, color: 'var(--text3)' }}>
                    {a.createdBy ? `${a.createdBy.firstName} ${a.createdBy.lastName}` : '—'}
                  </td>
                  <td>
                    {(isSuperAdmin || isSelf) && (
                      <button className="btn btn-sm btn-secondary" onClick={() => setEditId(a._id)}>
                        Edit
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {showInvite && (
        <InviteModal
          onClose={() => setShowInvite(false)}
          onCreated={a => { setAdmins(prev => [a, ...prev]); }}
        />
      )}

      {editId && (
        <EditModal
          adminId={editId}
          currentAdminId={currentAdmin?._id}
          onClose={() => setEditId(null)}
          onUpdated={load}
        />
      )}
    </AdminLayout>
  );
}
